import React, { useState, useRef, useCallback, useEffect } from "react";
import { GoogleMap, useLoadScript, OverlayView } from "@react-google-maps/api";
import { Ship, ChevronUp, ChevronDown } from "lucide-react";

const getStatusColor = (status) => {
  switch (status) {
    case "Anchorage":
      return "#cc9b09ff";
    case "Sailing":
      return "#178635ff";
    case "At Port":
      return "#2b6eebff";
    default:
      return "#eb2b35ff";
  }
};

const getStatusGlowColor = (status) => {
  switch (status) {
    case "Anchorage":
      return "#b68a09ff";
    case "Sailing":
      return "#0e752aff";
    case "At Port":
      return "#2c549eff";
    default:
      return "#eb2b35ff";
  }
};

const FleetMap = ({ vessels = [] }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [mapVessels, setMapVessels] = useState([]);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  useEffect(() => {
    if (vessels.length > 0) {
      const mappedVessels = vessels
        .filter(
          (vessel) => vessel.position?.latitude && vessel.position?.longitude
        )
        .map((vessel) => ({
          lat: vessel.position.latitude,
          lng: vessel.position.longitude,
          name: vessel.vesselName,
          status: vessel.status,
          performance: vessel.performance,
        }));

      console.log("Total vessels:", vessels.length);
      console.log("Vessels with position data:", mappedVessels.length);
      console.log(
        "Vessels without position data:",
        vessels.length - mappedVessels.length
      );

      setMapVessels(mappedVessels);
    } else {
      setMapVessels([]);
    }
  }, [vessels]);

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
    map.setOptions({ scrollwheel: false });

    map.addListener("mousedown", () => {
      map.setOptions({ scrollwheel: true });
    });

    map.addListener("mouseout", () => {
      map.setOptions({ scrollwheel: false });
    });
  }, []);

  const mapContainerStyle = {
    width: "100%",
    height: "500px",
    borderRadius: "12px",
  };

  if (loadError) return <p>Error loading maps</p>;
  if (!isLoaded) return <p>Loading Maps...</p>;

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div
        className="flex justify-between items-center px-4 py-4 cursor-pointer bg-gradient-to-r from-[#1a325d] to-[#27ade3]"
        onClick={() => setIsOpen(!isOpen)}
      >
        {/* Left Section - Title */}
        <h2 className="text-white text-lg font-normal">
          Fleet <span className="font-semibold">Map</span>
        </h2>

        {/* Right Section - Legend + Button */}
        <div className="flex items-center gap-4">
          {/* Legend */}
          <div className="flex items-center gap-4">
            {/* Sailing */}
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#178635ff]"></span>
              <span className="text-white text-sm">Sailing</span>
            </div>
            {/* At Port */}
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#2b6eebff]"></span>
              <span className="text-white text-sm">At Port</span>
            </div>
            {/* Anchorage */}
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#cc9b09ff]"></span>
              <span className="text-white text-sm">Anchorage</span>
            </div>
          </div>

          {/* Toggle Button */}
          <button className="p-1">
            {isOpen ? (
              <ChevronUp size={20} color="#fff" />
            ) : (
              <ChevronDown size={20} color="#fff" />
            )}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="bg-gradient-to-tr from-white to-[#d9ebff] p-4">
          <div className="relative">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={{ lat: 10, lng: 0 }}
              zoom={2.4}
              onLoad={handleMapLoad}
              options={{
                mapTypeId: "satellite",
                restriction: {
                  latLngBounds: {
                    north: 60,
                    south: -45,
                    west: -160,
                    east: 150,
                  },
                },
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                zoomControl: false,
                rotateControl: false,
                scaleControl: false,
                gestureHandling: "auto",
                draggable: true,
                scrollwheel: false,
                disableDoubleClickZoom: false,
                keyboardShortcuts: false,
              }}
            >
              {mapVessels.map((vessel, index) => (
                <OverlayView
                  key={`${vessel.name}-${index}`}
                  position={{ lat: vessel.lat, lng: vessel.lng }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div className="group relative flex items-center gap-2 cursor-pointer">
                    <div className="relative flex items-center justify-center">
                      {/* <div className="absolute w-10 h-10 rounded-full bg-cyan-400 opacity-30 animate-ping" /> */}
                      {/* <div className="z-10 flex items-center justify-center bg-[#1a325d] p-1.5 rounded-full shadow-md drop-shadow-[0_0_6px_#27ade3]">
                        <Ship className="text-cyan-300 w-4 h-4" />
                      </div> */}
                      <div
                        className="z-10 flex items-center justify-center p-1.5 rounded-full shadow-md"
                        style={{
                          backgroundColor: getStatusColor(
                            vessel.status?.status
                          ),
                          boxShadow: `0 0 6px ${getStatusGlowColor(
                            vessel.status?.status
                          )}`,
                        }}
                      >
                        <Ship className="text-white w-4 h-4" />
                      </div>
                    </div>
                    <span className="text-white text-[12px] whitespace-nowrap bg-[#1a325d] px-3 py-1 rounded">
                      {vessel.name}
                    </span>
                    <div className="absolute -top-20 left-8 -translate-x-1/2 scale-95 opacity-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200 ease-out flex flex-col items-center z-50">
                      <div className="flex flex-col gap-2 bg-black/60 text-white text-[12px] px-4 py-2 rounded shadow-md">
                        <p className="whitespace-nowrap font-bold">
                          {vessel.name}
                        </p>
                        <p className="whitespace-nowrap">
                          Latitude: {vessel.lat.toFixed(2)}°
                        </p>
                        <p className="whitespace-nowrap">
                          Longitude: {vessel.lng.toFixed(2)}°
                        </p>
                        {/* {vessel.status?.speedOverGround && (
                          <p className="whitespace-nowrap">Speed: {vessel.status.speedOverGround} kn</p>
                        )} */}
                        {/* {vessel.performance?.scores?.total && (
                          <p className="whitespace-nowrap">
                            Score: {vessel.performance.scores.total}%
                          </p>
                        )} */}
                      </div>
                      <div className="w-2 h-2 bg-black/60 rotate-45 mt-[-4px]" />
                    </div>
                  </div>
                </OverlayView>
              ))}
            </GoogleMap>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetMap;
